import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
} from "@material-ui/core";
import Alert from "@mui/material/Alert";
import { 
  IconUsers, 
  IconSquareNumber1, 
  IconSquareNumber2, 
  IconSquareNumber3, 
  IconDownload 
} from '@tabler/icons';
import { ExportarPlantillaCargaMasiva } from "../../actions/cargaMasivaAction";
import './styles/CargaMasiva.css';

function CardsMasiva(props) {
  const params = useParams();
  const navigate = useNavigate();
  
  // Estados
  const [descargando, setDescargando] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: "", text: "" });
  
  const contratoElegido = JSON.parse(localStorage.getItem("contratoElegido"));

  // ========== FUNCIONES DE ALERTAS ==========
  const showAlert = (type, text) => {
    setAlertMessage({ type, text });
    setTimeout(() => {
      setAlertMessage({ type: "", text: "" });
    }, 5000);
  };

  const clearAlert = () => {
    setAlertMessage({ type: "", text: "" });
  };

  // ========== FUNCIÓN PARA DESCARGAR BLOB ==========
  const downloadBlob = (blob, fileName) => {
    if (!(blob instanceof Blob)) {
      console.error("downloadBlob: No es un Blob válido", blob);
      return;
    }

    const a = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(objectUrl);
  };

  // ========== FUNCIÓN PRINCIPAL DE DESCARGA ==========
  const descargarPlantillaExcel = async () => {
    try {
      setDescargando(true);
      console.log(`📥 Iniciando descarga de plantilla para contrato ${params.id}...`);

      // Llamada al backend
      const response = await ExportarPlantillaCargaMasiva(params.id);
      const fileBlob = response.data;

      if (!(fileBlob instanceof Blob)) {
        throw new Error("Respuesta inválida del servidor");
      }

      // Generar nombre del archivo
      const nrContrato = contratoElegido?.nrContrato || params.id;
      const fechaArchivo = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `CargaMasiva-${nrContrato}-${fechaArchivo}.xlsx`;
      
      downloadBlob(fileBlob, fileName);

      console.log(`✅ Plantilla descargada exitosamente: ${fileName}`);
      showAlert("success", "Plantilla Excel descargada exitosamente");

    } catch (error) {
      console.error("Error descargando plantilla:", error);

      // Manejo de errores específicos
      if (error.response?.status === 404) {
        showAlert("warning", "No se encontró información para generar la plantilla");
      } else if (error.response?.status === 401) {
        showAlert("error", "Sesión expirada. Por favor, inicia sesión nuevamente");
        setTimeout(() => {
          window.location = "/";
        }, 2000);
      } else if (error.response?.status === 500) {
        showAlert("error", "Error interno del servidor. Intente nuevamente más tarde");
      } else {
        showAlert("error", `Error al descargar: ${error.message || "Error desconocido"}`);
      }
    } finally {
      setDescargando(false);
    }
  };

  // ========== NAVEGACIÓN ==========
  const routeChange = () => {
    navigate(`/auth/contratos/registrarhrspersona/cargamasiva/${params.id}`);
  };

  // ========== VERIFICAR ESTADO DEL CONTRATO ==========
  const esContratoActivo = 
    contratoElegido?.estado?.id === 2 ||
    contratoElegido?.estado?.id === 3 ||
    contratoElegido?.estado?.id === 6;

  // ========== RENDERIZADO ==========
  return (
    <section className={`caja-estilos-SeleccionarContrato${esContratoActivo ? '-CargaMasiva' : ''}`}>
      
      {/* ALERTA FLOTANTE */}
      {alertMessage.text && (
        <Alert
          severity={alertMessage.type}
          sx={{
            position: "fixed",
            top: "85px",
            left: "260px",
            right: "20px",
            zIndex: 1400,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
          }}
          onClose={clearAlert}
        >
          {alertMessage.text}
        </Alert>
      )}

      {/* HEADER */}
      <header  className="caja-estilos-SeleccionarContrato-header">
        <IconUsers 
          size={20} 
          color="white" 
          stroke={2.5} 
          style={{ marginRight: 5 }} 
        />
        Carga masiva
      </header>
      
      {/* CONTENIDO PRINCIPAL */}
      <main>
        <div className={`caja-estilos-SeleccionarContrato-contenido${esContratoActivo ? '-CargaMasiva' : ''}`}>
          
          {/* INSTRUCCIONES */}
          <div className="caja-estilos-SeleccionarContrato-contenido-textos">
            <ul>
              {/* PASO 1: DESCARGAR */}
              <li className="contenido-fila-cargaIndividual">
                <IconSquareNumber1
                  size={20}
                  color="black"
                  stroke={2}
                  style={{ marginRight: 5 }}
                />
                Descarga la plantilla de excel.
                
                {/* BOTÓN DE DESCARGA CON LOADING */}
                <Button
                  data-testid="download-button"
                  onClick={descargarPlantillaExcel}
                  disabled={descargando}
                  variant="contained"
                  style={{
                    backgroundColor: descargando ? "#ccc" : "#F77F00",
                    color: "white",
                    minWidth: "40px",
                    padding: "6px 12px",
                    marginLeft: "10px",
                    cursor: descargando ? "not-allowed" : "pointer"
                  }}
                >
                  {descargando ? (
                    <CircularProgress size={18} style={{ color: "white" }} />
                  ) : (
                    <IconDownload size={18} color="white" stroke={2} />
                  )}
                </Button>
              </li>
              
              {/* PASO 2: COMPLETAR */}
              <li className="contenido-fila-cargaIndividual">
                <IconSquareNumber2
                  size={20}
                  color="black"
                  stroke={2}
                  style={{ marginRight: 5 }}
                />
                Completa los campos según tus datos.
              </li>
              
              {/* PASO 3: SUBIR */}
              <li className="contenido-fila-cargaIndividual">
                <IconSquareNumber3
                  size={20}
                  color="black"
                  stroke={2}
                  style={{ marginRight: 5 }}
                />
                Sube tu plantilla al sistema.
              </li>
            </ul>
          </div>
          
          {/* BOTÓN SELECCIONAR */}
          <div className="caja-estilos-SeleccionarContrato-contenido-boton">
            <Button
              variant="contained"
              color={esContratoActivo ? "primary" : "secondary"}
              className={esContratoActivo ? "boton_seleccionar" : "boton_seleccionardisabled"}
              type="submit"
              disabled={!esContratoActivo}
              onClick={() => esContratoActivo && routeChange()}
            >
              Seleccionar
            </Button>
          </div>
        </div>
      </main>
    </section>
  );
}

export default CardsMasiva;